import {
  Progress as ProgressPrimitive,
  type ProgressRootProps,
  type UseProgressContext,
} from "@ark-ui/react";
import clsx from "clsx";
import * as React from "react";

interface FormatterState
  extends Pick<
    UseProgressContext,
    "value" | "percent" | "percentAsString" | "max" | "min"
  > {}

interface ProgressProps extends ProgressRootProps {
  size?: "xs" | "sm" | "lg";
  hideLabel?: boolean;
  type?: "circular" | "bar";
  valueFormatter?: (state: FormatterState) => React.ReactNode;
  color?: "red" | "green";
  iconIndicator?: boolean;
  invalid?: boolean;
  classNames?: {
    root?: string;
    svg?: string;
    indicator?: string;
    track?: string;
    value?: string;
  };
}

const Progress = React.forwardRef<
  React.ElementRef<typeof ProgressPrimitive.Root>,
  ProgressProps
>((props, ref) => {
  const {
    type = "bar",
    color = "green",
    size = "lg",
    hideLabel = false,
    invalid = false,
    iconIndicator = false,
    classNames,
    valueFormatter,
    ...rootProps
  } = props;

  return (
    <ProgressPrimitive.Root
      ref={ref}
      className={clsx("ox-reset ox-ProgressRoot", classNames?.root)}
      data-type={type}
      {...rootProps}
    >
      <ProgressPrimitive.Context>
        {({ value, percent, percentAsString, max, min }) => (
          <React.Fragment>
            {type === "circular" ? (
              <ProgressPrimitive.Circle
                className="ox-reset ox-ProgressCircle"
                data-size={size}
              >
                <ProgressPrimitive.CircleTrack
                  strokeLinecap="round"
                  className={clsx(
                    "ox-reset ox-ProgressCircleTrack",
                    classNames?.track,
                  )}
                />
                <ProgressPrimitive.CircleRange
                  strokeLinecap="round"
                  data-color={color}
                  className={clsx(
                    "ox-reset ox-ProgressCircleRange",
                    classNames?.indicator,
                  )}
                />
              </ProgressPrimitive.Circle>
            ) : (
              <ProgressPrimitive.Track
                className={clsx("ox-reset ox-ProgressTrack", classNames?.track)}
              >
                <ProgressPrimitive.Range
                  data-color={color}
                  className={clsx(
                    "ox-reset ox-ProgressRange",
                    classNames?.indicator,
                  )}
                />
              </ProgressPrimitive.Track>
            )}
            {!hideLabel ? (
              <React.Fragment>
                {iconIndicator && value === 100 ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    data-color={color}
                    data-size={size}
                    className={clsx("ox-ProgressComplete", classNames?.svg)}
                    viewBox="0 0 256 256"
                  >
                    <path d="M229.66,77.66l-128,128a8,8,0,0,1-11.32,0l-56-56a8,8,0,0,1,11.32-11.32L96,188.69,218.34,66.34a8,8,0,0,1,11.32,11.32Z"></path>
                  </svg>
                ) : invalid ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    data-color={color}
                    data-size={size}
                    className={clsx("ox-ProgressComplete", classNames?.svg)}
                    viewBox="0 0 256 256"
                  >
                    <path d="M205.66,194.34a8,8,0,0,1-11.32,11.32L128,139.31,61.66,205.66a8,8,0,0,1-11.32-11.32L116.69,128,50.34,61.66A8,8,0,0,1,61.66,50.34L128,116.69l66.34-66.35a8,8,0,0,1,11.32,11.32L139.31,128Z"></path>
                  </svg>
                ) : (
                  <ProgressPrimitive.ValueText
                    className={clsx(
                      "ox-reset ox-ProgressValue",
                      classNames?.value,
                    )}
                    data-type={type}
                    data-size={size}
                  >
                    {valueFormatter
                      ? valueFormatter({
                          value,
                          percent,
                          percentAsString,
                          max,
                          min,
                        })
                      : percentAsString}
                  </ProgressPrimitive.ValueText>
                )}
              </React.Fragment>
            ) : null}
          </React.Fragment>
        )}
      </ProgressPrimitive.Context>
    </ProgressPrimitive.Root>
  );
});

Progress.displayName = "Progress";
export { Progress };
export type { ProgressProps };
